import React, { useMemo, useState } from 'react';
import { Baby, CalendarHeart, Dog, Edit2, Heart, Loader2, Plus, Trash2, X } from 'lucide-react';
import { api } from '../../services/api';
import { todayLocal } from '../../utils/formatters';

// Camadas (partos) y celos/montas del criadero. La gestación canina promedio son 63 días
// desde la monta; el servidor calcula el parto probable, acá solo se muestra.
const GESTATION_DAYS = 63;

const HEAT_STATUS: Record<string, { label: string; cls: string }> = {
    heat: { label: 'En celo', cls: 'bg-rose-100 text-rose-600' },
    mated: { label: 'Montada', cls: 'bg-amber-100 text-amber-600' },
    pregnant: { label: 'Gestando', cls: 'bg-violet-100 text-violet-600' },
    born: { label: 'Parió', cls: 'bg-emerald-100 text-emerald-600' },
    failed: { label: 'Sin preñez', cls: 'bg-slate-100 text-slate-500' },
    cancelled: { label: 'Cancelado', cls: 'bg-slate-100 text-slate-400' },
};

const parseLocal = (d?: string | null) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`) : null);
const fmtDate = (d?: string | null) => {
    const p = parseLocal(d);
    return p && !isNaN(p.getTime()) ? p.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
};
const addDaysStr = (d: string, days: number) => {
    const p = parseLocal(d)!;
    p.setDate(p.getDate() + days);
    return `${p.getFullYear()}-${String(p.getMonth() + 1).padStart(2, '0')}-${String(p.getDate()).padStart(2, '0')}`;
};
const daysFromToday = (d?: string | null) => {
    const p = parseLocal(d);
    if (!p) return null;
    const t = parseLocal(todayLocal())!;
    return Math.round((p.getTime() - t.getTime()) / 86400000);
};

const inputCls = 'w-full bg-slate-50 border-2 border-slate-100 p-3 rounded-[7px] outline-none focus:border-[#FF6B00] font-bold text-sm transition-all';
const labelCls = 'block text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1.5 ml-1';

interface Props {
    pets: any[];
    litters: any[];
    heats: any[];
    asUser?: string | number;
    onReload: () => Promise<void> | void;
    onAddPuppy: (litter: any) => void;
    onViewPet: (pet: any) => void;
    /** Se llama cuando el plan Gratis no permite crear más (camadas / celos). */
    onPlanLimit?: (message: string) => void;
}

export const BreederLitters: React.FC<Props> = ({ pets, litters, heats, asUser, onReload, onAddPuppy, onViewPet, onPlanLimit }) => {
    const [heatForm, setHeatForm] = useState<any | null>(null);
    const [litterForm, setLitterForm] = useState<any | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Solo ejemplares que siguen en el criadero pueden ser padres.
    const owned = useMemo(() => pets.filter(p => p.owned !== false && p.status !== 'entregado'), [pets]);
    const females = owned.filter(p => p.gender === 'Hembra' || p.gender === 'Female');
    const males = owned.filter(p => p.gender === 'Macho' || p.gender === 'Male');
    const puppiesOf = (litterId: string) => pets.filter(p => String(p.litterId) === String(litterId));

    const closeForms = () => { setHeatForm(null); setLitterForm(null); setError(''); };

    const submit = async (fn: () => Promise<any>) => {
        setSaving(true);
        setError('');
        try {
            await fn();
            closeForms();
            await onReload();
        } catch (e: any) {
            if (e.code === 'PLAN_LIMIT' && onPlanLimit) { closeForms(); onPlanLimit(e.message); }
            else setError(e.message || 'No se pudo guardar');
        } finally {
            setSaving(false);
        }
    };

    const openHeat = (h?: any) => setHeatForm(h
        ? { id: h.id, motherId: h.motherId || '', motherName: h.motherId ? '' : (h.motherName || ''), fatherId: h.fatherId || '', fatherName: h.fatherId ? '' : (h.fatherName || ''), heatStart: h.heatStart || '', matingDate: h.matingDate || '', status: h.status, notes: h.notes || '' }
        : { motherId: '', motherName: '', fatherId: '', fatherName: '', heatStart: todayLocal(), matingDate: '', status: 'heat', notes: '' });

    const openLitter = (l?: any, fromHeat?: any) => setLitterForm(l
        ? { id: l.id, name: l.name || '', birthDate: l.birthDate || todayLocal(), fatherId: l.fatherId || '', motherId: l.motherId || '', fatherExternal: l.fatherId ? '' : (l.fatherName || ''), motherExternal: l.motherId ? '' : (l.motherName || ''), notes: l.notes || '', litterSize: l.litterSize ?? '' }
        : {
            name: '', birthDate: todayLocal(), notes: '', litterSize: '',
            fatherId: fromHeat?.fatherId || '', motherId: fromHeat?.motherId || '',
            fatherExternal: fromHeat?.fatherId ? '' : (fromHeat?.fatherName || ''), motherExternal: fromHeat?.motherId ? '' : (fromHeat?.motherName || ''),
            heatId: fromHeat?.id,
        });

    const removeHeat = async (h: any) => {
        if (!confirm('¿Eliminar este registro de celo/monta?')) return;
        await submit(() => api.breeder.deleteHeat(h.id, asUser));
    };
    const removeLitter = async (l: any) => {
        if (!confirm(`¿Eliminar la camada${l.name ? ` "${l.name}"` : ''}? Los cachorros quedan en la manada, solo se desvinculan de la camada.`)) return;
        await submit(() => api.breeder.deleteLitter(l.id, asUser));
    };

    const activeHeats = heats.filter(h => !['born', 'failed', 'cancelled'].includes(h.status));
    const pastHeats = heats.filter(h => ['born', 'failed', 'cancelled'].includes(h.status));

    const renderHeat = (h: any) => {
        const st = HEAT_STATUS[h.status] || HEAT_STATUS.heat;
        const left = daysFromToday(h.expectedBirth);
        const open = !['born', 'failed', 'cancelled'].includes(h.status);
        return (
            <div key={h.id} className="p-5 bg-white rounded-[2rem] border border-slate-100 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-sm font-black text-slate-800 uppercase italic truncate">{h.motherName || 'Hembra'}</p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest truncate">{h.fatherName ? `Macho: ${h.fatherName}` : 'Macho sin definir'}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest shrink-0 ${st.cls}`}>{st.label}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                    <div className="bg-slate-50 rounded-[7px] p-2"><p className="text-[8px] font-black text-slate-400 uppercase">Celo</p><p className="text-[11px] font-black text-slate-700">{fmtDate(h.heatStart)}</p></div>
                    <div className="bg-slate-50 rounded-[7px] p-2"><p className="text-[8px] font-black text-slate-400 uppercase">Monta</p><p className="text-[11px] font-black text-slate-700">{fmtDate(h.matingDate)}</p></div>
                    <div className="bg-violet-50 rounded-[7px] p-2"><p className="text-[8px] font-black text-violet-400 uppercase">Parto probable</p><p className="text-[11px] font-black text-violet-600">{fmtDate(h.expectedBirth)}</p></div>
                </div>
                {open && left !== null && (
                    <p className={`mt-3 text-[10px] font-black uppercase tracking-widest text-center ${left < 0 ? 'text-rose-500' : left <= 7 ? 'text-amber-500' : 'text-slate-400'}`}>
                        {left > 1 ? `Faltan ${left} días` : left === 1 ? 'Mañana' : left === 0 ? 'Parto probable hoy' : `Pasó hace ${Math.abs(left)} días`}
                    </p>
                )}
                {h.notes && <p className="mt-2 text-[11px] text-slate-500 font-medium italic">{h.notes}</p>}
                <div className="flex gap-2 mt-4">
                    {open && (
                        <button onClick={() => openLitter(undefined, h)} className="flex-1 bg-emerald-500 text-white py-2.5 rounded-[7px] text-[10px] font-black uppercase flex items-center justify-center gap-1.5 hover:bg-emerald-600 transition-colors">
                            <Baby size={14} /> Registrar parto
                        </button>
                    )}
                    <button onClick={() => openHeat(h)} className="p-2.5 bg-slate-100 text-slate-500 rounded-[7px] hover:bg-slate-200 transition-colors" title="Editar"><Edit2 size={14} /></button>
                    <button onClick={() => removeHeat(h)} className="p-2.5 bg-rose-50 text-rose-400 rounded-[7px] hover:bg-rose-100 transition-colors" title="Eliminar"><Trash2 size={14} /></button>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-10 animate-in fade-in duration-300">
            {/* ── Celos y gestaciones ── */}
            <section>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                    <div>
                        <h2 className="text-2xl font-black text-slate-800 uppercase italic tracking-tighter flex items-center gap-2"><CalendarHeart className="text-rose-500" size={24} /> Celos y montas</h2>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Seguimiento de la gestación · {GESTATION_DAYS} días desde la monta</p>
                    </div>
                    <button onClick={() => openHeat()} className="bg-rose-500 text-white px-5 py-3 rounded-[7px] font-black uppercase text-[10px] flex items-center justify-center gap-2 shadow-lg shadow-rose-500/20 hover:scale-105 transition-transform">
                        <Plus size={16} /> Registrar celo / monta
                    </button>
                </div>
                {activeHeats.length === 0 ? (
                    <div className="py-10 text-center bg-slate-50 rounded-[2rem] border-2 border-dashed border-slate-200">
                        <Heart size={32} className="mx-auto text-slate-300 mb-2" />
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">No hay celos ni gestaciones en curso</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{activeHeats.map(renderHeat)}</div>
                )}
                {pastHeats.length > 0 && (
                    <details className="mt-4">
                        <summary className="text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer">Historial ({pastHeats.length})</summary>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-3">{pastHeats.map(renderHeat)}</div>
                    </details>
                )}
            </section>

            {/* ── Camadas ── */}
            <section>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                    <div>
                        <h2 className="text-2xl font-black text-slate-800 uppercase italic tracking-tighter flex items-center gap-2"><Baby className="text-emerald-500" size={24} /> Camadas</h2>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Partos registrados y sus cachorros ({litters.length})</p>
                    </div>
                    <button onClick={() => openLitter()} className="bg-emerald-600 text-white px-5 py-3 rounded-[7px] font-black uppercase text-[10px] flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/20 hover:bg-emerald-700 hover:scale-105 transition-all">
                        <Plus size={16} /> Nueva camada
                    </button>
                </div>
                {litters.length === 0 ? (
                    <div className="py-10 text-center bg-slate-50 rounded-[2rem] border-2 border-dashed border-slate-200">
                        <Baby size={32} className="mx-auto text-slate-300 mb-2" />
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Aún no registras camadas</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {litters.map(l => {
                            const kids = puppiesOf(l.id);
                            return (
                                <div key={l.id} className="p-5 bg-white rounded-[2rem] border border-slate-100 shadow-sm">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="text-base font-black text-slate-800 uppercase italic truncate">{l.name || `Camada ${fmtDate(l.birthDate)}`}</p>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Nacieron el {fmtDate(l.birthDate)}</p>
                                        </div>
                                        <div className="flex gap-1.5 shrink-0">
                                            <button onClick={() => openLitter(l)} className="p-2 bg-slate-100 text-slate-500 rounded-[7px] hover:bg-slate-200" title="Editar"><Edit2 size={14} /></button>
                                            <button onClick={() => removeLitter(l)} className="p-2 bg-rose-50 text-rose-400 rounded-[7px] hover:bg-rose-100" title="Eliminar"><Trash2 size={14} /></button>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2 mt-3">
                                        <span className="px-3 py-1 bg-sky-50 text-sky-600 rounded-full text-[9px] font-black uppercase">Padre: {l.fatherName || 'Sin dato'}</span>
                                        <span className="px-3 py-1 bg-rose-50 text-rose-500 rounded-full text-[9px] font-black uppercase">Madre: {l.motherName || 'Sin dato'}</span>
                                        {l.litterSize != null && (
                                            <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-[9px] font-black uppercase">{l.litterSize} nacido{l.litterSize !== 1 ? 's' : ''}</span>
                                        )}
                                        <span className="px-3 py-1 bg-slate-100 text-slate-500 rounded-full text-[9px] font-black uppercase">{l.puppiesCount} con perfil · {l.males}♂ {l.females}♀</span>
                                    </div>
                                    {l.notes && <p className="mt-2 text-[11px] text-slate-500 font-medium italic">{l.notes}</p>}
                                    <div className="flex flex-wrap items-center gap-2 mt-4">
                                        {kids.map(k => (
                                            <button key={k.id} onClick={() => onViewPet(k)} className="flex items-center gap-2 pl-1 pr-3 py-1 bg-slate-50 rounded-full border border-slate-100 hover:border-orange-300 transition-colors" title={k.name}>
                                                <span className="w-7 h-7 rounded-full overflow-hidden bg-slate-200 flex items-center justify-center shrink-0">
                                                    {k.imageUrl ? <img src={k.imageUrl} className="w-full h-full object-cover" /> : <Dog size={14} className="text-slate-400" />}
                                                </span>
                                                <span className="text-[10px] font-black text-slate-600 uppercase">{k.name}</span>
                                            </button>
                                        ))}
                                        <button onClick={() => onAddPuppy(l)} className="flex items-center gap-1.5 px-3 py-1.5 border-2 border-dashed border-slate-200 rounded-full text-[10px] font-black text-slate-400 uppercase hover:border-orange-400 hover:text-orange-500 transition-colors">
                                            <Plus size={12} /> Cachorro
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            {/* ── Formulario celo / monta ── */}
            {heatForm && (
                <div className="fixed inset-0 z-[140] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm sm:p-4" onClick={closeForms}>
                    <div className="bg-white w-full sm:max-w-lg rounded-t-[2.5rem] sm:rounded-[2.5rem] p-6 sm:p-8 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-black text-slate-800 uppercase italic">{heatForm.id ? 'Editar celo / monta' : 'Registrar celo / monta'}</h3>
                            <button onClick={closeForms} className="p-2 bg-slate-100 rounded-[7px] text-slate-400"><X size={18} /></button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className={labelCls}>Hembra</label>
                                <select className={inputCls} value={heatForm.motherId} onChange={e => setHeatForm({ ...heatForm, motherId: e.target.value })}>
                                    <option value="">Otra (no está en mi manada)</option>
                                    {females.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                                </select>
                                {!heatForm.motherId && <input className={`${inputCls} mt-2`} placeholder="Nombre de la hembra" value={heatForm.motherName} onChange={e => setHeatForm({ ...heatForm, motherName: e.target.value })} />}
                            </div>
                            <div>
                                <label className={labelCls}>Macho (si ya hubo monta)</label>
                                <select className={inputCls} value={heatForm.fatherId} onChange={e => setHeatForm({ ...heatForm, fatherId: e.target.value })}>
                                    <option value="">Otro / externo / sin definir</option>
                                    {males.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                                </select>
                                {!heatForm.fatherId && <input className={`${inputCls} mt-2`} placeholder="Nombre del macho (opcional)" value={heatForm.fatherName} onChange={e => setHeatForm({ ...heatForm, fatherName: e.target.value })} />}
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><label className={labelCls}>Inicio del celo</label><input type="date" className={inputCls} value={heatForm.heatStart} onChange={e => setHeatForm({ ...heatForm, heatStart: e.target.value })} /></div>
                                <div><label className={labelCls}>Fecha de monta</label><input type="date" className={inputCls} value={heatForm.matingDate} onChange={e => setHeatForm({ ...heatForm, matingDate: e.target.value, status: heatForm.status === 'heat' && e.target.value ? 'mated' : heatForm.status })} /></div>
                            </div>
                            {heatForm.matingDate && (
                                <p className="text-[11px] font-black text-violet-600 bg-violet-50 rounded-[7px] px-4 py-3 uppercase tracking-wide">
                                    Parto probable: {fmtDate(addDaysStr(heatForm.matingDate, GESTATION_DAYS))}
                                </p>
                            )}
                            <div>
                                <label className={labelCls}>Estado</label>
                                <select className={inputCls} value={heatForm.status} onChange={e => setHeatForm({ ...heatForm, status: e.target.value })}>
                                    {Object.entries(HEAT_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                </select>
                            </div>
                            <div><label className={labelCls}>Notas</label><textarea rows={2} className={`${inputCls} resize-none`} value={heatForm.notes} onChange={e => setHeatForm({ ...heatForm, notes: e.target.value })} /></div>
                            {error && <p className="text-xs font-bold text-rose-500 bg-rose-50 rounded-[7px] px-4 py-3">{error}</p>}
                            <button disabled={saving} onClick={() => submit(() => api.breeder.saveHeat({
                                id: heatForm.id, motherId: heatForm.motherId, motherName: heatForm.motherName, fatherId: heatForm.fatherId, fatherName: heatForm.fatherName,
                                heatStart: heatForm.heatStart, matingDate: heatForm.matingDate, status: heatForm.status, notes: heatForm.notes,
                            }, asUser))} className="w-full bg-rose-500 text-white py-4 rounded-[7px] font-black uppercase text-xs tracking-widest disabled:opacity-50 flex items-center justify-center gap-2">
                                {saving ? <><Loader2 size={16} className="animate-spin" /> Guardando…</> : 'Guardar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Formulario camada ── */}
            {litterForm && (
                <div className="fixed inset-0 z-[140] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm sm:p-4" onClick={closeForms}>
                    <div className="bg-white w-full sm:max-w-lg rounded-t-[2.5rem] sm:rounded-[2.5rem] p-6 sm:p-8 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-black text-slate-800 uppercase italic">{litterForm.id ? 'Editar camada' : 'Nueva camada'}</h3>
                            <button onClick={closeForms} className="p-2 bg-slate-100 rounded-[7px] text-slate-400"><X size={18} /></button>
                        </div>
                        <div className="space-y-4">
                            <div><label className={labelCls}>Nombre de la camada (opcional)</label><input className={inputCls} placeholder="Ej: Camada Alpha" value={litterForm.name} onChange={e => setLitterForm({ ...litterForm, name: e.target.value })} /></div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><label className={labelCls}>Fecha de nacimiento</label><input type="date" className={inputCls} max={todayLocal()} value={litterForm.birthDate} onChange={e => setLitterForm({ ...litterForm, birthDate: e.target.value })} /></div>
                                <div><label className={labelCls}>Crías nacidas</label><input type="number" min={0} inputMode="numeric" className={inputCls} placeholder="Ej: 6" value={litterForm.litterSize} onChange={e => setLitterForm({ ...litterForm, litterSize: e.target.value.replace(/\D/g, '') })} /></div>
                            </div>
                            <div>
                                <label className={labelCls}>Padre</label>
                                <select className={inputCls} value={litterForm.fatherId} onChange={e => setLitterForm({ ...litterForm, fatherId: e.target.value })}>
                                    <option value="">Externo (no está en mi manada)</option>
                                    {males.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                                </select>
                                {!litterForm.fatherId && <input className={`${inputCls} mt-2`} placeholder="Nombre del padre" value={litterForm.fatherExternal} onChange={e => setLitterForm({ ...litterForm, fatherExternal: e.target.value })} />}
                            </div>
                            <div>
                                <label className={labelCls}>Madre</label>
                                <select className={inputCls} value={litterForm.motherId} onChange={e => setLitterForm({ ...litterForm, motherId: e.target.value })}>
                                    <option value="">Externa (no está en mi manada)</option>
                                    {females.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                                </select>
                                {!litterForm.motherId && <input className={`${inputCls} mt-2`} placeholder="Nombre de la madre" value={litterForm.motherExternal} onChange={e => setLitterForm({ ...litterForm, motherExternal: e.target.value })} />}
                            </div>
                            <div><label className={labelCls}>Notas</label><textarea rows={2} className={`${inputCls} resize-none`} value={litterForm.notes} onChange={e => setLitterForm({ ...litterForm, notes: e.target.value })} /></div>
                            {error && <p className="text-xs font-bold text-rose-500 bg-rose-50 rounded-[7px] px-4 py-3">{error}</p>}
                            <button disabled={saving || !litterForm.birthDate} onClick={() => submit(() => api.breeder.saveLitter(litterForm, asUser))}
                                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-4 rounded-[7px] font-black uppercase text-xs tracking-widest disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
                                {saving ? <><Loader2 size={16} className="animate-spin" /> Guardando…</> : 'Guardar camada'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BreederLitters;
